import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/States';
import { cn } from '@/lib/cn';
import { useBoard } from './hooks';
import { DoorToDoctor, FlagIcons, IdentityBadge, LevelBand, LevelPill, STATUS_LABEL, minutesLabel } from './ui';
import TriagePanel from './TriagePanel';
import { DoctorPageStatus, drName } from './DoctorAlertPopup';
import type { BoardRow } from './types';

/**
 * The ED Tracking Board (design §7). One row per patient, sorted server-side
 * by acuity then breach risk (utils/erBoard.js) — an untriaged arrival sits
 * above every level. Clocks and flags are computed on the server from the
 * encounter's own timestamps; this screen only renders them, and refetches
 * every 30 s.
 *
 * Casualty-lite mode shows a compact list (a small hospital's casualty room
 * has a duty doctor, not bays); full ED mode adds bay and attending columns.
 */
export default function TrackingBoard({ onOpen, onNewArrival, canRegister }: {
  onOpen: (id: number) => void;
  onNewArrival: () => void;
  canRegister: boolean;
}) {
  const board = useBoard(12);
  const [triageFor, setTriageFor] = useState<BoardRow | null>(null);
  const [showRecent, setShowRecent] = useState(false);

  if (board.isLoading) return <LoadingState label="Loading the board…" />;
  if (board.isError) return <ErrorState message={(board.error as Error).message} onRetry={() => board.refetch()} />;
  const data = board.data!;
  const full = data.mode === 'full';

  const tile = (label: string, value: number, tone?: 'danger' | 'warn') => (
    <div className={cn('rounded-xl border border-border bg-card px-4 py-3', tone === 'danger' && value > 0 && 'border-danger bg-danger-soft', tone === 'warn' && value > 0 && 'bg-warn-soft')}>
      <div className="text-[22px] font-bold leading-none text-ink">{value}</div>
      <div className="mt-1 text-[11px] uppercase tracking-wide text-ink-muted">{label}</div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {tile('In the ED', data.summary.total)}
        {tile('Awaiting triage', data.summary.awaiting_triage, 'warn')}
        {tile('Waiting for doctor', data.summary.waiting_for_doctor)}
        {tile('Door-to-doctor breaches', data.summary.breaches, 'danger')}
        {tile('Observation', data.summary.in_observation)}
        {tile('Boarding', data.summary.boarding, 'warn')}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[12px] text-ink-muted">
          {full ? 'Full ED' : 'Casualty-lite'} mode · updated {new Date(data.generated_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => setShowRecent((s) => !s)}>{showRecent ? 'Hide' : 'Show'} departed (12 h)</Button>
          {canRegister && <Button variant="danger" onClick={onNewArrival}>🚑 New arrival</Button>}
        </div>
      </div>

      {data.rows.length === 0 ? (
        <EmptyState label="No patients in the Emergency Department" hint="New arrivals appear here immediately." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full min-w-[900px] border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-border bg-muted/60 text-left text-[11px] uppercase tracking-wide text-ink-muted">
                <th className="w-2 p-0" />
                {full && <th className="px-3 py-2">Bay</th>}
                <th className="px-3 py-2">Patient</th>
                <th className="px-3 py-2">Complaint</th>
                <th className="px-3 py-2">Level</th>
                <th className="px-3 py-2">In ED</th>
                <th className="px-3 py-2">Door → doctor</th>
                <th className="px-3 py-2">Status</th>
                {full && <th className="px-3 py-2">Attending</th>}
                <th className="px-3 py-2">Alerts</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const urgent = r.clocks.flags.includes('door_to_doctor_breach') || r.clocks.flags.includes('retriage_overdue');
                return (
                  <tr key={r.id} onClick={() => onOpen(r.id)}
                    className={cn('cursor-pointer border-b border-border last:border-b-0 hover:bg-muted/50', urgent && 'bg-danger-soft/40')}>
                    <td className="p-0 py-1 pl-1"><LevelBand color={r.level_color} className="h-10" /></td>
                    {full && <td className="px-3 py-2 font-semibold">{r.bay_code ?? '—'}</td>}
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center gap-1.5 font-semibold text-ink">
                        {r.patient_name}
                        <IdentityBadge status={r.identity_status} />
                      </div>
                      <div className="text-[11px] text-ink-muted">
                        {r.er_number} · {r.uhid}{r.age != null ? ` · ${r.age}y` : r.approx_age_years != null ? ` · ~${r.approx_age_years}y` : ''}{r.gender ? ` · ${r.gender}` : ''}
                      </div>
                    </td>
                    <td className="max-w-[220px] truncate px-3 py-2" title={r.chief_complaint ?? ''}>{r.chief_complaint ?? <span className="text-ink-muted">—</span>}</td>
                    <td className="px-3 py-2"><LevelPill color={r.level_color} label={r.current_triage_level ? (r.level_label ?? r.current_triage_level) : null} /></td>
                    <td className="px-3 py-2 font-mono">{minutesLabel(r.clocks.minutes_since_arrival)}</td>
                    <td className="px-3 py-2"><DoorToDoctor clocks={r.clocks} seen={!!r.first_doctor_at} /></td>
                    <td className="px-3 py-2">
                      <div>{STATUS_LABEL[r.status]}</div>
                      {r.clocks.boarding && <div className="text-[11px] text-ink-muted">boarding {minutesLabel(r.clocks.boarding.minutes)}</div>}
                      {r.clocks.observation && <div className="text-[11px] text-ink-muted">obs {minutesLabel(r.clocks.observation.minutes_left)} left</div>}
                      {r.clocks.retriage?.overdue && <div className="text-[11px] font-semibold text-danger-ink">re-triage {minutesLabel(r.clocks.retriage.overdue_min)} overdue</div>}
                      <DoctorPageStatus status={r.alert_status} pagedAt={r.alert_paged_at}
                        declineReason={r.alert_decline_reason} className="mt-1" />
                      {r.alert_status === 'declined' && (
                        <div className="text-[11px] font-semibold text-danger-ink">
                          {drName(r.attending_doctor_name)}: {r.alert_decline_reason} — choose another doctor
                        </div>
                      )}
                    </td>
                    {full && <td className="px-3 py-2">{r.attending_doctor_name ?? <span className="text-ink-muted">—</span>}</td>}
                    <td className="px-3 py-2"><FlagIcons flags={r.clocks.flags} /></td>
                    <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                      {['arrived', 'triaged'].includes(r.status) && (
                        <Button size="sm" variant={r.triaged_at ? 'ghost' : 'primary'} onClick={() => setTriageFor(r)}>
                          {r.triaged_at ? 'Re-triage' : 'Triage'}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showRecent && (
        <div className="rounded-xl border border-border bg-card p-3">
          <div className="mb-2 text-[12px] font-semibold text-ink-muted">Departed in the last 12 hours</div>
          {data.recent.length === 0 ? <div className="text-[12px] text-ink-muted">None.</div> : (
            <div className="flex flex-wrap gap-2">
              {data.recent.map((r) => (
                <button key={r.id} type="button" onClick={() => onOpen(r.id)} className="rounded-lg border border-border px-2.5 py-1 text-left text-[12px] hover:bg-muted">
                  <b>{r.patient_name}</b> · {r.er_number} <Badge variant="neutral" className="ml-1">{r.disposition ?? 'departed'}</Badge>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {triageFor && <TriagePanel encounter={triageFor} open onClose={() => setTriageFor(null)} />}
    </div>
  );
}
